import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
// Relative rather than Helm's @/ paths, so the API's tests can render it.
import { apiClient } from '../../../lib/api/client'
import {
  DEFAULT_PREFERENCE,
  TranslationClient,
  parsePreference,
  preferenceKey,
  type SendTranslations,
  type TranslationPreference,
  type TranslationTarget,
} from '../../../lib/translation'

export interface TranslationSettings {
  client: TranslationClient
  /** The language the person reads Helm in. */
  target: TranslationTarget
  setTarget: (target: TranslationTarget) => void
  /** Translate page: every translatable block on the page shows its translation. */
  pageOn: boolean
  setPageOn: (on: boolean) => void
}

const TranslationContext = createContext<TranslationSettings | null>(null)

const sendThroughApi: SendTranslations = async (texts, target, context) =>
  (await apiClient.translate(texts, target, context)).translations.map((entry) => ({
    text: entry.text,
    sourceLanguage: entry.source_language,
    translated: entry.translated,
  }))

function readPreference(userId: string): TranslationPreference {
  try {
    return parsePreference(window.localStorage.getItem(preferenceKey(userId)))
  } catch {
    return DEFAULT_PREFERENCE
  }
}

function keepPreference(userId: string, preference: TranslationPreference) {
  try {
    window.localStorage.setItem(preferenceKey(userId), JSON.stringify(preference))
  } catch {
    // A private window keeps nothing; the choice still holds for this visit.
  }
}

/**
 * Live translation for one signed-in person: the language they read Helm in
 * and whether they translate whole pages, both remembered for them, and one
 * client that batches every block's request and remembers the answers for the
 * visit. Helm has no language setting of its own yet, so English until they
 * pick 中文 or Français. Without this provider nothing offers translation.
 */
export function TranslationProvider({
  userId,
  client,
  children,
}: {
  userId: string
  /** Stands in for the API's client in tests. */
  client?: TranslationClient
  children: ReactNode
}) {
  // Mounted per person (keyed by their id where it is used): someone else
  // signing in gets their own choice and nothing remembered from before.
  const [current, setState] = useState(() => ({
    userId,
    preference: readPreference(userId),
    client: client ?? new TranslationClient(sendThroughApi),
  }))

  const update = useCallback(
    (patch: Partial<TranslationPreference>) =>
      setState((previous) => {
        const preference = { ...previous.preference, ...patch }
        keepPreference(previous.userId, preference)
        return { ...previous, preference }
      }),
    [],
  )

  const value = useMemo<TranslationSettings>(
    () => ({
      client: current.client,
      target: current.preference.target,
      pageOn: current.preference.page,
      setTarget: (target) => update({ target }),
      setPageOn: (page) => update({ page }),
    }),
    [current.client, current.preference, update],
  )

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>
}

/** The person's translation settings; null outside a signed-in page, which offers none. */
export function useTranslationSettings(): TranslationSettings | null {
  return useContext(TranslationContext)
}
